/**
 * What the §F6 forms are allowed to offer — the §D5 catalogue, filtered per Part H.
 *
 * One module rather than a filter per form, because every one of these predicates is a
 * *refusal the backend would make anyway*, and a picker that offers a layer the tool
 * refuses turns a form into a failed job the reader cannot act on:
 *
 * * **`has_data`** (§D5, M4 Task 1) — a registered layer with no published version behind
 *   it has no COG for `rasters.layer_cog` to open and no rows for `vector.layer_table`;
 * * **`access !== 'none'`** — §I3 requires ≥LIMITED on every input, so a NONE row is a 403
 *   before the tool runs. (The server already omits NONE rows for public/field/partner, so
 *   this only bites analyst/manager, which is exactly when it must.)
 * * **`cog_titiler` only** for a raster: a `wms_external` layer is `display_only` (§1a rule
 *   4) — the pixels are never stored here, so there is nothing to run statistics over;
 * * **`is_categorical`** — §H3 refuses a class raster (T4), §H5 requires one.
 *
 * §H7 (buffer) and §H8 (overlay) left the dock in Task 10 (client items 11/12) — a UI-only
 * removal, so their picker rules (`analysisVectors`, the `wpdens` no-geometry exclusion,
 * the two tools' default layers and §H8's raster-mask presets) went with the only forms
 * that read them; the routes themselves are untouched.
 */
import { useQuery } from '@tanstack/react-query';

import { fetchLayers, queryKeys, type Layer, type UnitType } from '../../lib/api';
import { useAppStore } from '../../lib/state';

/**
 * §C's three `unit_type` branches, in the ENUM's own order. Shared by Compare (§H1, client
 * item 8) and Suitability (§H4, client item 4, Task 6) — one `Seg` over the same three
 * branches in both tools, so a reader who has learned the order in one dock finds it
 * unchanged in the other.
 */
export const UNIT_TYPES: UnitType[] = ['admin', 'watershed', 'partnership'];

/** Compare's own labels — reused by Suitability's `Seg` rather than a second translation
 * table for the same three words (see `Suitability.tsx`). */
export const TYPE_KEY: Record<UnitType, string> = {
  admin: 'analysis.compare.typeAdmin',
  watershed: 'analysis.compare.typeWatershed',
  partnership: 'analysis.compare.typePartnership',
};

/** The §D5 catalogue for the current role, as every pane reads it. */
export function useAnalysisLayers(): Layer[] {
  const role = useAppStore((s) => s.role);
  const { data } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });
  return data ?? [];
}

const usable = (layer: Layer): boolean =>
  !layer.unavailable && layer.access !== 'none' && layer.has_data !== false;

/** Rasters an analysis tool can actually open — see the module header. */
export const analysisRasters = (layers: readonly Layer[]): Layer[] =>
  layers.filter((l) => l.kind === 'cog_titiler' && usable(l));

/** §H3's picker: the same list without the class rasters the tool refuses. */
export const continuousRasters = (layers: readonly Layer[]): Layer[] =>
  analysisRasters(layers).filter((l) => !l.is_categorical);

/**
 * §H5's two epoch selects. **This is the whole of the epoch catalogue** — there is no
 * `/analysis/epochs` route, deliberately: §D5 already carries `epoch` and is already
 * access-filtered, and a second list could disagree with the first (T6).
 */
export const epochRasters = (layers: readonly Layer[]): Layer[] =>
  analysisRasters(layers)
    .filter((l) => l.is_categorical && typeof l.epoch === 'number')
    .sort((a, b) => (a.epoch ?? 0) - (b.epoch ?? 0));

/**
 * Pick a starting value for a select: the caller's preference if it is still on offer,
 * else the first option, else the empty string. Written once because more than one form
 * needs it and a select left pointing at a layer that has gone is a form that submits an id
 * the catalogue no longer has.
 */
export function firstAvailable(
  options: readonly Layer[],
  ...preferred: (string | null | undefined)[]
): string {
  for (const id of preferred) {
    if (id && options.some((l) => l.id === id)) return id;
  }
  return options[0]?.id ?? '';
}
